import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import {
  FullscreenReferenceType,
  GlobalLocationStateType,
} from '../../types/types';
import Reference from './components/Reference';
import { useHistory, useLocation, useParams } from 'react-router';
import useRoutes from '../../hooks/useRoutes';
import toast from 'react-hot-toast';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';

interface DatasetReferenceProps {
  fullscreenReference: FullscreenReferenceType | null;
  setFullscreenReference: Dispatch<
    SetStateAction<FullscreenReferenceType | null>
  >;
}

const DatasetReference: FC<DatasetReferenceProps> = ({
  fullscreenReference,
  setFullscreenReference,
}) => {
  const { state, pathname } = useLocation<GlobalLocationStateType>();
  const { datasetId: paramDatasetId } = useParams<{ datasetId: string }>();
  const { setActiveFilters, setLazyState, setSlaveLazyStates } =
    useDatasetContext();
  const history = useHistory();
  const routes = useRoutes();
  const { t } = useTranslate();

  const handleCloseFullscreenReference = () => {
    setFullscreenReference(null);
    const fallbackRoute = routes.datasetList(paramDatasetId);

    history.push(state.fromPathname ?? fallbackRoute);
  };

  const handleOnSubmit = (value: string) => {
    const filterName = state.filterItemName;
    const matchMode = state.matchMode;
    const columnName = fullscreenReference?.name;
    const datasetId = fullscreenReference?.datasetId;

    if (state.type === 'master' && filterName && matchMode && columnName) {
      setActiveFilters((oldActiveFilters) => ({
        ...oldActiveFilters,
        [filterName]: { [matchMode]: value },
      }));
      setLazyState((oldLazyState) => {
        const newFilters = { ...(oldLazyState.filters as Record<string, any>) };
        newFilters[columnName] = {
          operator: 'and',
          constraints: [{ value, matchMode }],
        };
        return { ...oldLazyState, filters: newFilters };
      });

      handleCloseFullscreenReference();
      return;
    }

    if (state.type === 'slave' && columnName && datasetId) {
      setSlaveLazyStates((oldSlaveLazyStates) => {
        const oldState = oldSlaveLazyStates as Record<string, any>;
        const newLazyState = {
          ...oldState[datasetId],
          filters: {
            ...oldState[datasetId].filters,
            [columnName]: {
              operator: 'and',
              constraints: [{ value, matchMode }],
            },
          },
        };
        return { ...oldState, [datasetId]: newLazyState };
      });

      handleCloseFullscreenReference();
      return;
    }

    toast(t('toasts.invalidReference'));
    handleCloseFullscreenReference();
    return;
  };

  useEffect(() => {
    if (!state?.fromPathname) {
      const newPath = pathname.replace('/reference', '');
      history.push(newPath);
    }
    // Redirect back to the dataset page if user reloads the fullscreen reference page
    if (!fullscreenReference) {
      handleCloseFullscreenReference();
    }
  }, []);

  if (!fullscreenReference) {
    return null;
  }

  return (
    <Reference
      fullscreenReference={fullscreenReference}
      handleCloseFullscreenReference={handleCloseFullscreenReference}
      handleOnSubmit={handleOnSubmit}
    />
  );
};

export default DatasetReference;
